import { Box, Chip } from '@mui/material';
import { isNil } from 'lodash';
import { ReactNode } from 'react';

import { ProcessingSource } from '@/api/historicalData';
import { Timestamp, TimestampProps, TimestampRange, TimestampRangeProps } from '@/components/data/Timestamp';
import { boldWeight } from '@/theme';

export type DataDetailSectionTimestampProps = {
    timestamp: TimestampProps['value'];
    processingSource?: ProcessingSource | null;
    includeSeconds?: boolean;
};

const DataDetailTimestampContainer = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{ position: 'sticky', top: 0, zIndex: 1, containerType: 'scroll-state', containerName: 'sticky-container' }}
    >
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                fontWeight: boldWeight,
                p: 1,
                gap: 0.5,
                alignItems: 'center',
                backgroundColor: '#d4d4d4',
                '@container sticky-container scroll-state(stuck: top)': {
                    boxShadow: 1,
                },
            }}
        >
            {children}
        </Box>
    </Box>
);

export const DataDetailSectionTimestamp = ({
    timestamp,
    processingSource,
    includeSeconds,
}: DataDetailSectionTimestampProps) => {
    if (isNil(timestamp)) return false;

    return (
        <DataDetailTimestampContainer>
            <Timestamp variant="caption" value={timestamp} type="short" includeSeconds={includeSeconds} />
            <ProcessingSourceTag processingSource={processingSource} />
        </DataDetailTimestampContainer>
    );
};

const ProcessingSourceTag = ({ processingSource }: { processingSource?: ProcessingSource | null }) => {
    const isHdr = processingSource === 'hdr';

    if (!isHdr) {
        return false;
    }
    return (
        <Chip
            label="HDR"
            color="info"
            size="small"
            sx={{
                flexShrink: 0,
                maxWidth: 'none',
                fontSize: '0.7rem',
                height: '14px',
                lineHeight: '14px',
                mt: '-1px',
                mr: '-2px',
                '& .MuiChip-label': { px: '6px' },
            }}
        />
    );
};

export const DataDetailTimestampRange = ({
    processingSource,
    ...props
}: {
    processingSource?: ProcessingSource | null;
} & Pick<TimestampRangeProps, 'dateRange' | 'unit' | 'includeSeconds'>) => {
    return (
        <DataDetailTimestampContainer>
            <TimestampRange {...props} variant="caption" />
            <ProcessingSourceTag processingSource={processingSource} />
        </DataDetailTimestampContainer>
    );
};
